import React, { useEffect, createElement } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Heart, Search, ShieldCheck, Sparkles, Lightbulb, FlaskConical, ScanLine, Target, Info } from 'lucide-react';
import { Link } from 'react-router-dom';
const story = [{
  icon: Lightbulb,
  title: 'Why We Built IntelliEats',
  paragraphs: ['IntelliEats was created for one simple reason: to help everyday shoppers become aware of the ingredients going into their packaged food. Most people genuinely want to eat well, but the fine print on a food label is rarely written for the average buyer.', 'Our goal is to give the general public an easy, transparent way to understand what a product actually contains — so that a healthier choice is only a quick scan away, no nutrition degree required.']
}, {
  icon: FlaskConical,
  title: 'Our Research Process',
  paragraphs: ['We spent months carefully collecting and reviewing ingredients commonly flagged as harmful or worth limiting. For each one, we studied what it is, why manufacturers use it, and — most importantly — its potential long-term side effects.', 'We then mapped how excessive, long-term consumption of certain additives has been associated with chronic conditions such as obesity, type 2 diabetes, heart disease, and high blood pressure. This research became the foundation that powers IntelliEats and keeps its guidance grounded and trustworthy.']
}, {
  icon: ScanLine,
  title: 'Reading Ingredient Labels as a Normal Buyer',
  paragraphs: ['As an ordinary buyer, we know first-hand how difficult it is to recognize every questionable ingredient hidden in a long, hard-to-pronounce list. Sugar alone can appear under a dozen different names, and preservatives, artificial colors, and flavor enhancers are easy to miss.', 'That real-world frustration is exactly where IntelliEats helps. Instead of decoding chemistry on the spot, you simply scan the label and instantly see which ingredients may be worth a second thought — explained in plain, everyday language.']
}, {
  icon: Target,
  title: 'Our Mission',
  paragraphs: ['Our mission is straightforward: to help people truly know what they are eating so they can live a healthier, more informed life. We believe awareness is the first, most powerful step toward better nutrition.', 'By making ingredient information clear and accessible, IntelliEats empowers you and your family to compare products confidently and build healthier habits that last — one scan, and one smarter choice, at a time.']
}];
export function AboutPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'About IntelliEats | Understand Food Ingredients & Eat Healthier';
    let desc = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
    if (!desc) {
      desc = document.createElement('meta');
      desc.name = 'description';
      document.head.appendChild(desc);
    }
    desc.content = 'Learn why we built IntelliEats — a free app that helps everyday shoppers scan packaged-food labels, understand harmful ingredients and their long-term health effects, and make informed, healthier choices.';
  }, []);
  const values = [{
    icon: Search,
    title: 'Research-Driven',
    description: 'Born from a passion for researching ingredients and uncovering the hidden dangers in everyday packaged foods.'
  }, {
    icon: Heart,
    title: 'Healthier Living',
    description: 'We help you understand what is really in your food so you can make choices that support a longer, healthier life.'
  }, {
    icon: ShieldCheck,
    title: 'Personalized Awareness',
    description: 'Dietary restriction settings let you personalize your scanner and follow your own diet with confidence.'
  }];
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} className="pt-32 pb-24 bg-white min-h-screen" data-id="element-37" data-mp="nvj3gnyr4yp1">
      
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-38" data-mp="9xm63pch4o9b">
        <header className="mb-12" data-id="element-39" data-mp="dvjxmytpvmfv">
          <Link to="/" className="inline-flex items-center text-sm font-medium text-brand-600 hover:text-brand-700 mb-8 transition-colors" data-id="element-40" data-mp="dyo5c66e50ba">
            
            <ArrowLeft className="w-4 h-4 mr-2" data-id="element-41" data-mp="1vv9p7zel3u4" />
            Back to Home
          </Link>
          <div className="flex items-center gap-4 mb-6" data-id="element-42" data-mp="3t86f7wh7813q">
            <div className="bg-brand-100 text-brand-600 p-3 rounded-2xl" data-id="element-43" data-mp="3oa4xbevie7tu">
              <Sparkles className="w-8 h-8" data-id="element-44" data-mp="3o9v83tiag4p7" />
            </div>
            <h1 className="text-4xl sm:text-5xl font-black text-slate-900 tracking-tight" data-id="element-45" data-mp="3k9kze934306n">
              About the App
            </h1>
          </div>
          <p className="text-lg text-slate-600 leading-relaxed" data-id="element-46" data-mp="3fbt7vg6dpg2z">
            Most people want to eat healthy — but understanding what's actually
            in your food shouldn't require a chemistry degree.
          </p>
        </header>

        {/* Main Story */}
        <div className="bg-gradient-to-br from-brand-50 to-accent-50/40 rounded-3xl p-8 sm:p-10 border border-brand-100 mb-12" data-id="element-47" data-mp="18ih0rkkeqhbk">
          <div className="space-y-5 text-slate-700 leading-relaxed text-lg" data-id="element-48" data-mp="13i338wgfmprs">
            <p data-id="element-49" data-mp="1c0o9csjclvwj">
              Most people want to eat healthy. However, when you go to a store
              and look at the ingredient label, it is full of ingredients you
              have never heard of and can't pronounce! How would you know if it
              is healthy or not?
            </p>
            <p data-id="element-50" data-mp="1prjlqet22v4v">
              This is a major problem. According to the FDA,{' '}
              <span className="font-medium text-slate-900" data-id="element-51" data-mp="t3dkm1vcekvc">
                "Diet-related chronic diseases are the leading causes of death
                and disability in the U.S."
              </span>
            </p>
            <p data-id="element-52" data-mp="121fjaarlt1fg">
              This app was born out of the passion of researching, helping
              others, promoting healthy eating, and the desire to help others
              understand and know the hidden dangers of ingredients in their
              daily packaged foods — ingredients that are often linked to
              long-term health issues.
            </p>
            <p data-id="element-53" data-mp="1srjrq69sp7rz">
              We have expanded our purpose by adding dietary restriction
              settings to help you personalize your own scanner and follow your
              own diet, along with getting awareness of what is in your food.
            </p>
            <p className="font-semibold text-brand-800" data-id="element-54" data-mp="1xs7j4t1xrqmq">
              We hope this app can help you make healthier choices and lead a
              healthier life :)
            </p>
          </div>
        </div>

        {/* SEO Story Sections */}
        <div className="space-y-6 mb-12" data-id="element-55" data-mp="154tudi8e61dp">
          {story.map((section, index) => <motion.section key={section.title} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-60px'
        }} transition={{
          duration: 0.4,
          delay: index * 0.05
        }} className="bg-white rounded-2xl p-6 sm:p-8 border border-slate-100 shadow-sm" data-id="element-56" data-mp="1jt850fc1uu4t" data-mpi={index}>
            
              <div className="flex items-center gap-4 mb-4" data-id="element-57" data-mp="2xzcn9lggb75x">
                <div className="w-11 h-11 rounded-xl bg-brand-50 text-brand-600 flex items-center justify-center shrink-0" data-id="element-58" data-mp="32qky8lxfj0l7">
                  <section.icon className="w-6 h-6" data-id="element-59" data-mp="2o2zrmi1cij8j" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900" data-id="element-60" data-mp="2t0i42iekngla">
                  {section.title}
                </h2>
              </div>
              <div className="space-y-4 text-slate-700 leading-relaxed" data-id="element-61" data-mp="3gr5fhdnp4ocl">
                {section.paragraphs.map((p, i) => <p key={i} data-id="element-62" data-mp="3loxgqya8dmtg" data-mpi={i}>{p}</p>)}
              </div>
            </motion.section>)}
        </div>

        {/* Not Medical Advice */}
        <section className="bg-slate-50 rounded-2xl p-6 sm:p-8 border border-slate-200 mb-12" data-id="element-63" data-mp="3briz4vckmrzk">
          <div className="flex items-center gap-3 mb-3" data-id="element-64" data-mp="1wasbdipr58uo">
            <div className="w-10 h-10 rounded-xl bg-slate-200/70 text-slate-600 flex items-center justify-center shrink-0" data-id="element-65" data-mp="1w97hlvb9htxv">
              <Info className="w-5 h-5" data-id="element-66" data-mp="20a9j6isnii30" />
            </div>
            <h2 className="text-xl font-bold text-slate-900" data-id="element-67" data-mp="2s10272d7g2mo">
              Helpful Guidance, Not Medical Advice
            </h2>
          </div>
          <p className="text-slate-600 leading-relaxed" data-id="element-68" data-mp="2n370ommujwsq">
            IntelliEats is designed to help you make informed, everyday food
            decisions by making ingredient information easier to understand. It
            is <span className="font-semibold text-slate-900" data-id="element-69" data-mp="3aq6sbagbvwii">not</span> a
            substitute for professional medical, nutritional, or dietary advice.
            For guidance tailored to your personal health needs, please consult
            a qualified healthcare professional or registered dietitian.
          </p>
        </section>

        {/* Values */}
        <div className="grid sm:grid-cols-3 gap-6 mb-12" data-id="element-70" data-mp="2wyrwg31bo0u1">
          {values.map((value, index) => <motion.div key={value.title} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.4,
          delay: index * 0.1
        }} className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm" data-id="element-71" data-mp="3ufz0d9zm8bj9" data-mpi={index}>
            
              <div className="w-12 h-12 rounded-xl bg-brand-50 text-brand-600 flex items-center justify-center mb-4" data-id="element-72" data-mp="5un98kactmpk">
                <value.icon className="w-6 h-6" data-id="element-73" data-mp="et8u4m3atdti" />
              </div>
              <h3 className="font-bold text-slate-900 mb-2" data-id="element-74" data-mp="eujyan0nonl5">{value.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed" data-id="element-75" data-mp="jriqh00rekgp">
                {value.description}
              </p>
            </motion.div>)}
        </div>

        {/* Developer Note */}
        <div className="text-center border-t border-slate-100 pt-10" data-id="element-76" data-mp="tjg5yiysvyl8">
          <p className="text-slate-500" data-id="element-77" data-mp="yhq2oy06b78y">
            Developed with care by{' '}
            <span className="font-semibold text-slate-900" data-id="element-78" data-mp="12itk3oxvdbca">IntelliEats Team</span>
          </p>
          <Link to="/#download" className="inline-flex items-center mt-6 px-6 py-3 bg-brand-600 text-white font-semibold rounded-full hover:bg-brand-700 transition-colors shadow-lg shadow-brand-200/50" data-id="element-79" data-mp="17hwco9wnxp3d">
            
            Download IntelliEats Free
          </Link>
        </div>
      </div>
    </motion.div>;
}